(() => {
  const componentCleanups = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 })
    : null;

  function register(root, setup) {
    if (!root || componentCleanups.has(root)) return;
    const cleanup = setup(root);
    componentCleanups.set(root, typeof cleanup === 'function' ? cleanup : () => {});
  }

  function cleanupWithin(scope) {
    [...componentCleanups.entries()].forEach(([root, cleanup]) => {
      if (root === scope || scope.contains(root)) {
        cleanup();
        componentCleanups.delete(root);
      }
    });
    if (revealObserver) {
      if (scope.matches?.('.reveal')) revealObserver.unobserve(scope);
      scope.querySelectorAll('.reveal').forEach(element => revealObserver.unobserve(element));
    }
  }

  function initReveal(scope) {
    const elements = [];
    if (scope.matches?.('.reveal')) elements.push(scope);
    elements.push(...scope.querySelectorAll?.('.reveal') || []);
    elements.forEach(element => {
      if (reducedMotion.matches || !revealObserver) element.classList.add('visible');
      else revealObserver.observe(element);
    });
  }

  function initHeader(root) {
    register(root, element => {
      const controller = new AbortController();
      const signal = controller.signal;
      const nav = element.querySelector('[data-header-nav]');
      const socialDock = element.querySelector('[data-social-dock]');
      const menuButton = element.querySelector('[data-menu-button]');
      const navLinks = element.querySelector('[data-nav-links]');
      const megaTrigger = element.querySelector('[data-controller-trigger]');
      const megaPanel = element.querySelector('[data-controller-mega]');
      const searchTrigger = element.querySelector('[data-search-trigger]');
      const searchPanel = element.querySelector('[data-search-panel]');
      const localeTrigger = element.querySelector('[data-locale-trigger]');
      const localePanel = element.querySelector('[data-locale-panel]');
      const helpWidget = element.querySelector('[data-help-widget]');
      const helpLaunch = helpWidget?.querySelector('[data-help-launch]');
      const helpPanel = helpWidget?.querySelector('[data-help-panel]');
      const crewWidget = element.querySelector('[data-crew-widget]');
      const crewLaunch = crewWidget?.querySelector('[data-crew-launch]');
      const crewPanel = crewWidget?.querySelector('[data-crew-panel]');
      const menuOpenLabel = menuButton?.dataset.openLabel || menuButton?.getAttribute('aria-label') || '';
      const menuCloseLabel = menuButton?.dataset.closeLabel || element.querySelector('[data-search-close], [data-locale-close], [data-mega-close]')?.textContent.trim() || menuOpenLabel;
      let closeTimer;
      let scrollFrame;
      const mobileHeaderMedia = window.matchMedia('(max-width: 900px)');
      const brandPlaque = element.querySelector('.brand-plaque');
      let previousScrollY = Math.max(0, window.scrollY);
      let scrollDirection = 0;
      let scrollDistance = 0;

      function setMobileHeaderHidden(hidden) {
        element.classList.toggle('is-mobile-scroll-hidden', hidden && mobileHeaderMedia.matches);
      }

      function setMenu(open, focus = false) {
        if (open) setMobileHeaderHidden(false);
        if (!menuButton || !navLinks) return;
        navLinks.classList.toggle('open', open);
        menuButton.setAttribute('aria-expanded', String(open));
        menuButton.setAttribute('aria-label', open ? menuCloseLabel : menuOpenLabel);
        if (focus) menuButton.focus();
      }

      function setMega(open, focus = false) {
        if (open) setMobileHeaderHidden(false);
        if (!megaTrigger || !megaPanel) return;
        clearTimeout(closeTimer);
        megaPanel.hidden = !open;
        megaTrigger.setAttribute('aria-expanded', String(open));
        if (open) closeOthers('mega');
        if (focus) megaTrigger.focus();
      }

      function setSearch(open, focus = false) {
        if (open) setMobileHeaderHidden(false);
        if (!searchTrigger || !searchPanel) return;
        searchPanel.hidden = !open;
        searchTrigger.setAttribute('aria-expanded', String(open));
        if (open) {
          closeOthers('search');
          requestAnimationFrame(() => searchPanel.querySelector('input[name="q"]')?.focus());
        }
        if (focus) searchTrigger.focus();
      }

      function setLocale(open, focus = false) {
        if (open) setMobileHeaderHidden(false);
        if (!localeTrigger || !localePanel) return;
        localePanel.hidden = !open;
        localeTrigger.setAttribute('aria-expanded', String(open));
        if (open) {
          closeOthers('locale');
          requestAnimationFrame(() => localePanel.querySelector('select')?.focus());
        }
        if (focus) localeTrigger.focus();
      }

      function setHelp(open, focus = false) {
        if (open) setMobileHeaderHidden(false);
        if (!helpLaunch || !helpPanel) return;
        helpPanel.hidden = !open;
        helpLaunch.setAttribute('aria-expanded', String(open));
        if (open) {
          closeOthers('help');
          requestAnimationFrame(() => helpPanel.querySelector('[data-help-close]')?.focus());
        }
        if (focus) helpLaunch.focus();
      }

      function setCrew(open, focus = false) {
        if (open) setMobileHeaderHidden(false);
        if (!crewLaunch || !crewPanel) return;
        crewPanel.hidden = !open;
        crewLaunch.setAttribute('aria-expanded', String(open));
        if (open) {
          closeOthers('crew');
          requestAnimationFrame(() => crewPanel.querySelector('[data-crew-close]')?.focus());
        }
        if (focus) crewLaunch.focus();
      }

      function closeOthers(except) {
        if (except !== 'menu' && !(except === 'mega' && window.matchMedia('(max-width: 900px)').matches && navLinks?.classList.contains('open'))) setMenu(false);
        if (except !== 'mega') setMega(false);
        if (except !== 'search') setSearch(false);
        if (except !== 'locale') setLocale(false);
        if (except !== 'help') setHelp(false);
        if (except !== 'crew') setCrew(false);
      }

      function delayMegaClose() {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => {
          if (!megaPanel?.matches(':hover') && !megaTrigger?.matches(':hover') && !megaPanel?.contains(document.activeElement) && document.activeElement !== megaTrigger) setMega(false);
        }, 160);
      }

      menuButton?.addEventListener('click', () => {
        const open = !navLinks.classList.contains('open');
        if (open) closeOthers('menu');
        setMenu(open);
      }, { signal });
      navLinks?.addEventListener('click', event => {
        if (event.target.closest('a')) setMenu(false);
      }, { signal });

      megaTrigger?.addEventListener('click', event => {
        const desktopMouseClick = event.detail > 0 && window.matchMedia('(min-width: 901px)').matches;
        setMega(desktopMouseClick || megaPanel.hidden);
      }, { signal });
      megaTrigger?.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' && window.matchMedia('(min-width: 901px)').matches) setMega(true);
      }, { signal });
      megaTrigger?.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse' && window.matchMedia('(min-width: 901px)').matches) delayMegaClose();
      }, { signal });
      megaPanel?.addEventListener('pointerenter', () => clearTimeout(closeTimer), { signal });
      megaPanel?.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse' && window.matchMedia('(min-width: 901px)').matches) delayMegaClose();
      }, { signal });
      megaPanel?.addEventListener('focusout', () => setTimeout(() => {
        if (!megaPanel.contains(document.activeElement) && document.activeElement !== megaTrigger) setMega(false);
      }), { signal });
      megaPanel?.querySelector('[data-mega-close]')?.addEventListener('click', () => setMega(false, true), { signal });
      megaPanel?.addEventListener('click', event => {
        if (event.target.closest('a')) setMega(false);
      }, { signal });

      searchTrigger?.addEventListener('click', () => setSearch(searchPanel.hidden), { signal });
      searchPanel?.querySelector('[data-search-close]')?.addEventListener('click', () => setSearch(false, true), { signal });
      localeTrigger?.addEventListener('click', () => setLocale(localePanel.hidden), { signal });
      localePanel?.querySelector('[data-locale-close]')?.addEventListener('click', () => setLocale(false, true), { signal });
      helpLaunch?.addEventListener('click', () => setHelp(helpPanel.hidden), { signal });
      helpPanel?.querySelector('[data-help-close]')?.addEventListener('click', () => setHelp(false, true), { signal });
      crewLaunch?.addEventListener('click', () => setCrew(crewPanel.hidden), { signal });
      crewPanel?.querySelector('[data-crew-close]')?.addEventListener('click', () => setCrew(false, true), { signal });

      document.addEventListener('pointerdown', event => {
        if (megaPanel && !megaPanel.hidden && !megaPanel.contains(event.target) && !megaTrigger.contains(event.target)) setMega(false);
        if (searchPanel && !searchPanel.hidden && !searchPanel.contains(event.target) && !searchTrigger.contains(event.target)) setSearch(false);
        if (localePanel && !localePanel.hidden && !localePanel.contains(event.target) && !localeTrigger.contains(event.target)) setLocale(false);
        if (helpPanel && !helpPanel.hidden && !helpWidget.contains(event.target)) setHelp(false);
        if (crewPanel && !crewPanel.hidden && !crewWidget.contains(event.target)) setCrew(false);
      }, { signal });

      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        if (megaPanel && !megaPanel.hidden) setMega(false, true);
        else if (searchPanel && !searchPanel.hidden) setSearch(false, true);
        else if (localePanel && !localePanel.hidden) setLocale(false, true);
        else if (helpPanel && !helpPanel.hidden) setHelp(false, true);
        else if (crewPanel && !crewPanel.hidden) setCrew(false, true);
        else if (navLinks?.classList.contains('open')) setMenu(false, true);
      }, { signal });

      function updateScroll() {
        const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const scrollY = Math.max(0, Math.min(window.scrollY, maxScrollY));
        const delta = scrollY - previousScrollY;
        const panelOpen = navLinks?.classList.contains('open') ||
          [megaPanel, searchPanel, localePanel, helpPanel, crewPanel].some(panel => panel && !panel.hidden);
        const keyboardFocus = nav?.querySelector(':focus-visible') || brandPlaque?.matches(':focus-visible');
        nav?.classList.toggle('scrolled', scrollY > 35);
        socialDock?.classList.toggle('is-scroll-hidden', scrollY > 70);
        if (!mobileHeaderMedia.matches || scrollY <= 80 || panelOpen || keyboardFocus) {
          setMobileHeaderHidden(false);
          scrollDirection = 0;
          scrollDistance = 0;
        } else if (delta !== 0) {
          const direction = Math.sign(delta);
          if (direction !== scrollDirection) scrollDistance = 0;
          scrollDirection = direction;
          scrollDistance += Math.abs(delta);
          if (scrollDistance >= 8) {
            setMobileHeaderHidden(direction > 0);
            scrollDistance = 0;
          }
        }
        previousScrollY = scrollY;
        scrollFrame = null;
      }
      function scheduleHeaderScroll() {
        if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScroll);
      }
      window.addEventListener('scroll', scheduleHeaderScroll, { passive: true, signal });
      mobileHeaderMedia.addEventListener('change', updateScroll, { signal });
      element.addEventListener('focusin', () => setMobileHeaderHidden(false), { signal });
      document.addEventListener('keydown', event => {
        if (event.key === 'Tab') setMobileHeaderHidden(false);
      }, { signal });
      updateScroll();

      return () => {
        controller.abort();
        clearTimeout(closeTimer);
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
      };
    });
  }

  function initHero(root) {
    register(root, element => {
      const controller = new AbortController();
      const signal = controller.signal;
      const turntable = element.querySelector('[data-hero-turntable]') || element.querySelector('.hero-img');
      const button = element.querySelector('[data-hero-turn]');
      const label = element.querySelector('[data-hero-turn-label]');
      const front = element.querySelector('[data-hero-front]');
      const back = element.querySelector('[data-hero-back]');
      const video = element.querySelector('.rocket-hero__video');
      const videoButton = element.querySelector('[data-hero-video-control]');
      const videoLabel = element.querySelector('[data-hero-video-label]');

      if (video) {
        function setVideoState(playing) {
          if (!videoButton) return;
          videoButton.setAttribute('aria-pressed', String(playing));
          const nextLabel = playing ? videoButton.dataset.pauseLabel || videoButton.dataset.defaultPauseLabel : videoButton.dataset.playLabel || videoButton.dataset.defaultPlayLabel;
          videoButton.setAttribute('aria-label', nextLabel);
          if (videoLabel) videoLabel.textContent = nextLabel;
        }

        videoButton?.addEventListener('click', () => {
          if (video.paused) video.play().catch(() => setVideoState(false));
          else video.pause();
        }, { signal });
        video.addEventListener('play', () => setVideoState(true), { signal });
        video.addEventListener('pause', () => setVideoState(false), { signal });
        if (reducedMotion.matches) video.pause();
        else if (video.autoplay) video.play().catch(() => setVideoState(false));
        setVideoState(!video.paused);
        return () => controller.abort();
      }

      if (!turntable || !button) return () => controller.abort();
      let showingBack = false;
      let angle = 0;
      let startX = 0;
      let startAngle = 0;
      let dragging = false;
      let scrollFrame;

      function setTurn(nextBack) {
        showingBack = nextBack;
        angle = nextBack ? 180 : 0;
        turntable.style.setProperty('--hero-turn', `${angle}deg`);
        button.setAttribute('aria-pressed', String(nextBack));
        if (label) label.textContent = nextBack ? button.dataset.frontLabel || button.dataset.defaultFrontLabel : button.dataset.backLabel || button.dataset.defaultBackLabel;
        front?.setAttribute('aria-hidden', String(nextBack));
        back?.setAttribute('aria-hidden', String(!nextBack));
      }

      button.addEventListener('click', () => setTurn(!showingBack), { signal });
      turntable.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        dragging = true;
        startX = event.clientX;
        startAngle = angle;
        turntable.classList.add('is-dragging');
        turntable.setPointerCapture(event.pointerId);
      }, { signal });
      turntable.addEventListener('pointermove', event => {
        if (!dragging) return;
        angle = Math.max(0, Math.min(180, startAngle - (event.clientX - startX) * 0.65));
        turntable.style.setProperty('--hero-turn', `${angle}deg`);
      }, { signal });
      const endTurn = () => {
        if (!dragging) return;
        dragging = false;
        turntable.classList.remove('is-dragging');
        setTurn(angle >= 90);
      };
      turntable.addEventListener('pointerup', endTurn, { signal });
      turntable.addEventListener('pointercancel', endTurn, { signal });
      turntable.addEventListener('lostpointercapture', endTurn, { signal });

      function updateZoom() {
        if (!reducedMotion.matches && window.scrollY < window.innerHeight) {
          turntable.style.transform = `translate(-50%,-50%) scale(${1 + window.scrollY / window.innerHeight * 0.22})`;
        } else if (reducedMotion.matches) {
          turntable.style.transform = '';
        }
        scrollFrame = null;
      }
      window.addEventListener('scroll', () => {
        if (!scrollFrame) scrollFrame = requestAnimationFrame(updateZoom);
      }, { passive: true, signal });
      updateZoom();
      return () => {
        controller.abort();
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
      };
    });
  }

  function initFeatureCinema(root) {
    register(root, element => {
      const controller = new AbortController();
      const signal = controller.signal;
      const tabs = [...element.querySelectorAll('[data-feature-index]')];
      const viewport = element.querySelector('[data-feature-viewport]');
      const video = element.querySelector('[data-feature-video]');
      const playButton = element.querySelector('[data-feature-play]');
      const playLabel = element.querySelector('[data-feature-play-label]');
      const title = viewport?.querySelector('[data-feature-title]');
      const description = viewport?.querySelector('[data-feature-description]');
      const counter = element.querySelector('[data-feature-counter]');
      const progress = element.querySelector('[data-feature-progress]');
      const progressbar = element.querySelector('[data-feature-progressbar]');
      const status = element.querySelector('[data-feature-status]');
      if (!tabs.length || !video || !playButton) return () => controller.abort();
      let active = Math.max(0, tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true'));
      let touchStart = null;
      let inView = false;
      let userPaused = false;
      const autoplay = !reducedMotion.matches;
      const observer = 'IntersectionObserver' in window
        ? new IntersectionObserver(entries => {
            entries.forEach(entry => {
              inView = entry.isIntersecting;
              if (inView && autoplay && !userPaused) playVideo();
              if (!inView) video.pause();
            });
          }, { threshold: 0.35 })
        : null;

      function setPlaying(playing) {
        const controlLabel = playing ? playButton.dataset.pauseLabel || 'Pause' : playButton.dataset.playLabel || 'Play video';
        playButton.classList.toggle('is-playing', playing);
        playButton.setAttribute('aria-label', controlLabel);
        if (playLabel) playLabel.textContent = controlLabel;
      }

      function playVideo() {
        video.play().catch(() => setPlaying(false));
      }

      function selectFeature(next, focusTab = false) {
        active = (next + tabs.length) % tabs.length;
        const tab = tabs[active];
        const featureTitle = tab.dataset.featureTitle || tab.querySelector('span')?.textContent.trim() || tab.textContent.trim();
        const featureDescription = tab.dataset.featureDescription || '';
        const source = tab.dataset.featureVideoUrl || '';
        const poster = tab.dataset.featurePosterUrl || '';
        tabs.forEach((item, index) => {
          const selected = index === active;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        if (focusTab) {
          tab.focus();
          tab.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
        }
        if (title) title.textContent = featureTitle;
        if (description) description.textContent = featureDescription;
        if (counter) counter.textContent = `${String(active + 1).padStart(2, '0')} / ${String(tabs.length).padStart(2, '0')}`;
        if (viewport && tab.id) viewport.setAttribute('aria-labelledby', tab.id);
        video.pause();
        video.classList.add('is-switching');
        if (status) status.hidden = true;
        if (progress) progress.style.width = '0%';
        progressbar?.setAttribute('aria-valuenow', '0');
        userPaused = false;
        if (source) video.setAttribute('src', source);
        else video.removeAttribute('src');
        if (poster) video.setAttribute('poster', poster);
        else video.removeAttribute('poster');
        video.classList.remove('rocket-image--auto', 'rocket-image--fill', 'rocket-image--original', 'rocket-image--portrait');
        video.classList.add(`rocket-image--${tab.dataset.featurePosterFit || 'auto'}`);
        video.load();
        video.setAttribute('aria-label', featureTitle);
        playButton.disabled = !source;
        if (!source) {
          video.classList.remove('is-switching');
          setPlaying(false);
          if (status) status.hidden = false;
        } else if (inView && autoplay) playVideo();
      }

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => selectFeature(index), { signal });
        tab.addEventListener('keydown', event => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
          selectFeature(active + (forward ? 1 : -1), true);
        }, { signal });
      });
      playButton.addEventListener('click', () => {
        userPaused = !video.paused;
        if (video.paused) {
          userPaused = false;
          playVideo();
        } else video.pause();
      }, { signal });
      video.addEventListener('playing', () => {
        video.classList.remove('is-switching');
        setPlaying(true);
      }, { signal });
      video.addEventListener('pause', () => setPlaying(false), { signal });
      video.addEventListener('loadeddata', () => {
        video.classList.remove('is-switching');
        if (status) status.hidden = true;
      }, { signal });
      video.addEventListener('timeupdate', () => {
        const percentage = Number.isFinite(video.duration) && video.duration > 0
          ? Math.max(0, Math.min(video.currentTime / video.duration * 100, 100))
          : 0;
        if (progress) progress.style.width = `${percentage}%`;
        progressbar?.setAttribute('aria-valuenow', String(Math.round(percentage)));
      }, { signal });
      video.addEventListener('error', () => {
        video.classList.remove('is-switching');
        setPlaying(false);
        if (status) status.hidden = false;
      }, { signal });
      viewport?.addEventListener('touchstart', event => {
        touchStart = event.changedTouches[0].clientX;
      }, { passive: true, signal });
      viewport?.addEventListener('touchend', event => {
        if (touchStart === null) return;
        const delta = event.changedTouches[0].clientX - touchStart;
        touchStart = null;
        if (Math.abs(delta) > 55) selectFeature(active + (delta < 0 ? 1 : -1));
      }, { passive: true, signal });
      observer?.observe(element);
      if (!observer) {
        inView = true;
      }
      selectFeature(active);
      return () => {
        observer?.disconnect();
        video.pause();
        controller.abort();
      };
    });
  }

  function initPerformance(root) {
    register(root, element => {
      const controller = new AbortController();
      const signal = controller.signal;
      if (!finePointer.matches || reducedMotion.matches) return () => controller.abort();
      element.querySelectorAll('[data-performance-card]').forEach(card => {
        card.addEventListener('pointermove', event => {
          const box = card.getBoundingClientRect();
          const x = (event.clientX - box.left) / box.width;
          const y = (event.clientY - box.top) / box.height;
          card.style.setProperty('--pointer-x', `${(x * 100).toFixed(1)}%`);
          card.style.setProperty('--pointer-y', `${(y * 100).toFixed(1)}%`);
          card.style.setProperty('--move-x', `${((x - 0.5) * 10).toFixed(1)}px`);
          card.style.setProperty('--move-y', `${((y - 0.5) * 8).toFixed(1)}px`);
        }, { signal });
        card.addEventListener('pointerleave', () => {
          ['--pointer-x', '--pointer-y', '--move-x', '--move-y'].forEach(property => card.style.removeProperty(property));
        }, { signal });
      });
      return () => controller.abort();
    });
  }

  function initModelQuiz(root) {
    register(root, quiz => {
      const controller = new AbortController();
      const signal = controller.signal;
      const steps = [...quiz.querySelectorAll('[data-quiz-step]')];
      const result = quiz.querySelector('[data-quiz-result]');
      const back = quiz.querySelector('[data-quiz-back]');
      const counter = quiz.querySelector('[data-quiz-counter]');
      const meter = quiz.querySelector('[data-quiz-meter]');
      if (!steps.length || !result || !back) return () => controller.abort();
      const answers = [];
      let current = 0;
      const total = steps.length;

      function render(focus) {
        steps.forEach((step, index) => { step.hidden = index !== current; });
        result.hidden = current !== total;
        back.hidden = current === 0;
        const displayStep = Math.min(current + 1, total);
        if (counter) counter.textContent = `${String(displayStep).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
        if (meter) meter.style.width = `${Math.min(total > 0 ? displayStep / total * 100 : 0, 100)}%`;
        if (focus) (current === total ? result : steps[current]).querySelector('h3')?.focus({ preventScroll: true });
      }

      function recommend() {
        const [games, intensity, budget, design] = answers;
        const type = games === 'shooter' ? (budget === 'value' ? 'force' : 'pro') : 'x';
        let reason;
        if (type === 'force') reason = intensity === 'competitive' ? quiz.dataset.forceCompetitive : quiz.dataset.forceCasual;
        else if (type === 'pro') reason = intensity === 'competitive' ? quiz.dataset.proCompetitive : quiz.dataset.proCasual;
        else reason = games === 'racing' ? quiz.dataset.xRacing : quiz.dataset.xMixed;
        const values = {
          force: [quiz.dataset.forceTitle, quiz.dataset.forceLabel, quiz.dataset.forceUrl],
          pro: [quiz.dataset.proTitle, quiz.dataset.proLabel, quiz.dataset.proUrl],
          x: [quiz.dataset.xTitle, quiz.dataset.xLabel, quiz.dataset.xUrl]
        };
        const [title, label, url] = values[type];
        result.querySelector('[data-quiz-title]').textContent = title;
        result.querySelector('[data-quiz-reason]').textContent = reason;
        const link = result.querySelector('[data-quiz-link]');
        link.textContent = design === 'expressive' ? quiz.dataset.buildLabel : label;
        link.href = design === 'expressive' ? quiz.dataset.buildUrl : url;
      }

      quiz.querySelectorAll('[data-quiz-choice]').forEach(button => button.addEventListener('click', () => {
        answers[current] = button.dataset.quizChoice;
        current += 1;
        if (current === total) recommend();
        render(true);
      }, { signal }));
      back.addEventListener('click', () => {
        if (current > 0) {
          current -= 1;
          render(true);
        }
      }, { signal });
      quiz.addEventListener('rocket:quiz-preview', event => {
        const previewStep = Number(event.detail?.step);
        if (!Number.isInteger(previewStep) || previewStep < 0 || previewStep >= total) return;
        current = previewStep;
        render(false);
      }, { signal });
      quiz.addEventListener('rocket:quiz-reset', () => {
        current = 0;
        render(false);
      }, { signal });
      render(false);
      return () => controller.abort();
    });
  }

  function initDesignGallery(root) {
    register(root, gallery => {
      const controller = new AbortController();
      const signal = controller.signal;
      const stage = gallery.querySelector('[data-design-stage]');
      const image = gallery.querySelector('[data-design-image]');
      const title = gallery.querySelector('[data-design-title]');
      const counter = gallery.querySelector('[data-design-counter]');
      const productLink = gallery.querySelector('[data-design-link]');
      const productHint = gallery.querySelector('[data-design-product-hint]');
      const buttons = [...gallery.querySelectorAll('.design-option')];
      if (!stage || !image || !buttons.length) return () => controller.abort();

      const preloadQueue = buttons.slice(1);
      const preloadedImages = [];
      let activePreloads = 0;
      let preloadingStarted = false;

      function preloadNext() {
        while (!signal.aborted && activePreloads < 2 && preloadQueue.length) {
          const button = preloadQueue.shift();
          if (!button.dataset.designImage) continue;
          const preload = new Image();
          preload.decoding = 'async';
          preload.fetchPriority = 'low';
          if (button.dataset.designSrcset) {
            preload.sizes = '(min-width: 901px) 64vw, 90vw';
            preload.srcset = button.dataset.designSrcset;
          }
          activePreloads++;
          const finish = () => {
            activePreloads--;
            preloadNext();
          };
          preload.addEventListener('load', finish, { once: true, signal });
          preload.addEventListener('error', finish, { once: true, signal });
          preload.src = button.dataset.designImage;
          preloadedImages.push(preload);
        }
      }

      function startPreloading() {
        if (preloadingStarted) return;
        preloadingStarted = true;
        preloadNext();
      }

      function selectDesign(button, focus = false) {
        buttons.forEach(item => {
          const selected = item === button;
          item.setAttribute('aria-pressed', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        const index = buttons.indexOf(button);
        const name = button.dataset.designName || button.textContent.trim();
        const productUrl = button.dataset.designUrl;
        if (productLink) {
          if (productUrl) {
            productLink.setAttribute('href', productUrl);
            productLink.setAttribute('aria-label', `${productLink.dataset.designLinkLabel}: ${name}`);
          } else {
            productLink.removeAttribute('href');
            productLink.removeAttribute('aria-label');
          }
        }
        if (productHint) {
          productHint.textContent = productUrl && productLink
            ? `${productLink.dataset.designLinkLabel} →`
            : productHint.dataset.defaultHint || '';
        }
        if (button.dataset.designSrcset) {
          image.srcset = button.dataset.designSrcset;
          image.sizes = '(min-width: 901px) 64vw, 90vw';
        } else {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
        }
        if (button.dataset.designImage) image.src = button.dataset.designImage;
        if (button.dataset.designWidth) image.width = Number(button.dataset.designWidth);
        if (button.dataset.designHeight) image.height = Number(button.dataset.designHeight);
        image.alt = button.dataset.designAlt || name;
        image.classList.remove('rocket-image--auto', 'rocket-image--fill', 'rocket-image--original', 'rocket-image--portrait');
        image.classList.add(`rocket-image--${button.dataset.designFit || 'auto'}`);
        if (title) title.textContent = name;
        if (counter) counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(buttons.length).padStart(2, '0')}`;
        stage.style.setProperty('--design-glow', button.dataset.designGlow || '#513160');
        if (!reducedMotion.matches) {
          image.classList.remove('design-switch');
          void image.offsetWidth;
          image.classList.add('design-switch');
        }
        if (focus) {
          button.focus();
          button.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
        }
      }

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => selectDesign(button), { signal });
        button.addEventListener('keydown', event => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          selectDesign(buttons[(index + direction + buttons.length) % buttons.length], true);
        }, { signal });
      });
      if (!reducedMotion.matches) {
        stage.addEventListener('pointermove', event => {
          if (event.pointerType === 'touch') return;
          const rect = stage.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width;
          const y = (event.clientY - rect.top) / rect.height;
          stage.style.setProperty('--tilt-x', `${((0.5 - y) * 5).toFixed(2)}deg`);
          stage.style.setProperty('--tilt-y', `${((x - 0.5) * 7).toFixed(2)}deg`);
          stage.style.setProperty('--light-x', `${(x * 100).toFixed(1)}%`);
          stage.style.setProperty('--light-y', `${(y * 100).toFixed(1)}%`);
        }, { signal });
        stage.addEventListener('pointerleave', () => {
          stage.style.setProperty('--tilt-x', '0deg');
          stage.style.setProperty('--tilt-y', '0deg');
          stage.style.setProperty('--light-x', '50%');
          stage.style.setProperty('--light-y', '42%');
        }, { signal });
      }
      selectDesign(buttons.find(button => button.getAttribute('aria-pressed') === 'true') || buttons[0]);
      if (image.complete && image.naturalWidth > 0) startPreloading();
      else image.addEventListener('load', startPreloading, { once: true, signal });
      return () => {
        controller.abort();
        preloadedImages.length = 0;
      };
    });
  }

  function initParallax(root) {
    register(root, section => {
      const controller = new AbortController();
      const signal = controller.signal;
      const stage = section.querySelector('[data-misaki-stage]');
      const button = section.querySelector('[data-misaki-motion]');
      const hapticSlide = section.querySelector('.motion-slide--haptic');
      if (!stage) return () => controller.abort();
      if (reducedMotion.matches) {
        button?.setAttribute('aria-pressed', 'false');
        return () => controller.abort();
      }
      let targetX = 0;
      let targetY = 0;
      let currentX = 0;
      let currentY = 0;
      let interacting = false;
      let visible = false;
      let frameId = null;
      let start = performance.now();
      let autoMotion = section.dataset.autoMotion === 'true';
      button?.setAttribute('aria-pressed', String(autoMotion));

      function setTarget(event) {
        const rect = stage.getBoundingClientRect();
        targetX = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - 0.5) * 2));
        targetY = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height - 0.5) * 2));
      }

      function frame(time) {
        if (!visible || document.hidden) {
          frameId = null;
          return;
        }
        const hapticActive = hapticSlide?.classList.contains('is-active');
        if (!interacting && autoMotion) {
          const seconds = (time - start) / 1000;
          targetX = Math.sin(seconds * 0.82) * (hapticActive ? 0.68 : 0.48);
          targetY = Math.cos(seconds * 0.63) * (hapticActive ? 0.24 : 0.18);
        } else if (!interacting) {
          targetX = 0;
          targetY = 0;
        }
        currentX += (targetX - currentX) * (hapticActive ? 0.19 : 0.075);
        currentY += (targetY - currentY) * (hapticActive ? 0.19 : 0.075);
        section.style.setProperty('--ry', `${(currentX * 6.4).toFixed(3)}deg`);
        section.style.setProperty('--rx', `${(-currentY * 4.4).toFixed(3)}deg`);
        section.style.setProperty('--lift', `${(-Math.abs(currentX) * 1.1 - Math.abs(currentY) * 0.5).toFixed(2)}px`);
        section.style.setProperty('--fx', `${(currentX * 8.6).toFixed(2)}px`);
        section.style.setProperty('--fy', `${(currentY * 2.1).toFixed(2)}px`);
        section.style.setProperty('--sx', `${(-currentX * 4.5).toFixed(2)}px`);
        section.style.setProperty('--sy', `${(-currentY * 1.15).toFixed(2)}px`);
        section.style.setProperty('--light', `${(50 + currentX * 58).toFixed(1)}%`);
        section.style.setProperty('--shadow-x', `${(currentX * 12).toFixed(2)}px`);
        section.style.setProperty('--haptic-ry', `${(currentX * 15.5).toFixed(3)}deg`);
        section.style.setProperty('--haptic-rx', `${(-currentY * 7.5).toFixed(3)}deg`);
        section.style.setProperty('--haptic-light-x', `${(currentX * 30).toFixed(2)}px`);
        section.style.setProperty('--haptic-light-y', `${(currentY * 10).toFixed(2)}px`);
        section.style.setProperty('--haptic-light-back-x', `${(-currentX * 15).toFixed(2)}px`);
        section.style.setProperty('--haptic-light-back-y', `${(-currentY * 5).toFixed(2)}px`);
        section.style.setProperty('--haptic-ink-front-x', `${(currentX * 7).toFixed(2)}px`);
        section.style.setProperty('--haptic-ink-back-x', `${(-currentX * 4).toFixed(2)}px`);
        section.style.setProperty('--haptic-ink-y', `${(currentY * 3).toFixed(2)}px`);
        const cleanOpacity = Math.max(0, Math.min(1, currentX * 5.4));
        section.style.setProperty('--haptic-clean-opacity', cleanOpacity.toFixed(3));
        section.style.setProperty('--haptic-ink-opacity', (Math.min(0.38, Math.max(0, -currentX * 0.65)) * (1 - cleanOpacity)).toFixed(3));
        frameId = requestAnimationFrame(frame);
      }

      function ensureFrame() {
        if (visible && !document.hidden && frameId === null) {
          start = performance.now();
          frameId = requestAnimationFrame(frame);
        }
      }

      function stopFrame() {
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
          frameId = null;
        }
      }

      button?.addEventListener('pointerdown', event => event.stopPropagation(), { signal });
      button?.addEventListener('click', event => {
        event.stopPropagation();
        autoMotion = !autoMotion;
        button.setAttribute('aria-pressed', String(autoMotion));
        if (!autoMotion) {
          targetX = 0;
          targetY = 0;
        }
        ensureFrame();
      }, { signal });
      stage.addEventListener('pointerenter', () => { interacting = true; }, { signal });
      stage.addEventListener('pointermove', event => { interacting = true; setTarget(event); ensureFrame(); }, { signal });
      stage.addEventListener('pointerdown', event => { interacting = true; setTarget(event); ensureFrame(); }, { signal });
      stage.addEventListener('pointerup', event => { if (event.pointerType === 'touch') interacting = false; }, { signal });
      stage.addEventListener('pointercancel', () => { interacting = false; }, { signal });
      stage.addEventListener('pointerleave', () => { interacting = false; }, { signal });
      stage.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        interacting = true;
        targetX = event.key === 'ArrowLeft' ? -0.72 : 0.72;
        targetY = 0;
        ensureFrame();
      }, { signal });
      stage.addEventListener('blur', () => { interacting = false; }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stopFrame();
        else ensureFrame();
      }, { signal });
      const observer = 'IntersectionObserver' in window
        ? new IntersectionObserver(entries => {
            entries.forEach(entry => {
              visible = entry.isIntersecting && entry.intersectionRatio > 0;
              if (visible) ensureFrame();
              else stopFrame();
            });
          }, { threshold: 0 })
        : null;
      if (observer) observer.observe(section);
      else {
        visible = true;
        ensureFrame();
      }
      return () => {
        observer?.disconnect();
        stopFrame();
        controller.abort();
      };
    });
  }

  function initCommunity(root) {
    register(root, section => {
      const controller = new AbortController();
      const signal = controller.signal;
      const tabs = [...section.querySelectorAll('[data-people-tab]')];
      const people = [...section.querySelectorAll('.rocket-people__person[data-people-name]')];
      const stage = section.querySelector('[data-people-stage]');
      const name = section.querySelector('.rocket-people__spotlight [data-people-name]');
      const kind = section.querySelector('[data-people-kind]');
      const portrait = section.querySelector('[data-people-portrait]');
      const counter = section.querySelector('[data-people-counter]');
      const listTitle = section.querySelector('[data-people-list-title]');
      const empty = section.querySelector('[data-people-empty]');
      const previous = section.querySelector('[data-people-prev]');
      const next = section.querySelector('[data-people-next]');
      const directory = section.querySelector('[data-people-directory]') || section.querySelector('.rocket-people__controls a');
      if (!tabs.length || !stage || !name) return () => controller.abort();
      const initialCategory = tabs.find(tab => tab.getAttribute('aria-selected') === 'true')?.dataset.peopleTab || section.dataset.defaultCategory || 'creator';
      const initialDirectoryHref = directory?.getAttribute('href') || '';
      const initialDirectoryLabel = directory?.textContent.trim() || '';
      const peopleDirectoryHref = section.dataset.peopleUrl || (initialCategory === 'player' ? '/pages/creators' : initialDirectoryHref);
      const peopleDirectoryLabel = section.dataset.peopleLinkLabel || (initialCategory === 'player' ? 'ALLE PARTNER & CREATORS' : initialDirectoryLabel);
      const playerDirectoryHref = section.dataset.playerUrl || (initialCategory === 'player' ? initialDirectoryHref : '/collections/all');
      const playerDirectoryLabel = section.dataset.playerLinkLabel || (initialCategory === 'player' ? initialDirectoryLabel : 'ROCKET CONTROLLER ENTDECKEN');
      const categoryMeta = {
        creator: {
          kind: section.dataset.creatorKind || 'CREATOR',
          listTitle: section.dataset.creatorListTitle || 'SELECT A CREATOR'
        },
        partner: {
          kind: section.dataset.partnerKind || 'PARTNERSHIP',
          listTitle: section.dataset.partnerListTitle || 'SELECT A PARTNER'
        },
        player: {
          kind: section.dataset.playerKind || 'ROCKET PLAYER',
          listTitle: section.dataset.playerListTitle || 'SELECT A PLAYER',
          emptyName: section.dataset.emptyName || ''
        }
      };
      if (categoryMeta[initialCategory]) {
        if (kind?.textContent.trim()) categoryMeta[initialCategory].kind = kind.textContent.trim();
        if (listTitle?.textContent.trim()) categoryMeta[initialCategory].listTitle = listTitle.textContent.trim();
        if (!people.some(person => person.dataset.peopleCategory === initialCategory) && name.textContent.trim()) {
          categoryMeta[initialCategory].emptyName = name.textContent.trim();
        }
      }
      let category = initialCategory;
      let index = 0;
      let startX = null;
      const currentPeople = () => people.filter(person => person.dataset.peopleCategory === category);

      function update() {
        const current = currentPeople();
        tabs.forEach(tab => {
          const selected = tab.dataset.peopleTab === category;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          const count = tab.querySelector('span');
          if (count) count.textContent = String(people.filter(person => person.dataset.peopleCategory === tab.dataset.peopleTab).length).padStart(2, '0');
        });
        people.forEach(person => {
          person.hidden = person.dataset.peopleCategory !== category;
          person.setAttribute('aria-pressed', 'false');
        });
        if (empty) empty.hidden = current.length > 0;
        if (previous) previous.disabled = current.length === 0;
        if (next) next.disabled = current.length === 0;
        const meta = categoryMeta[category] || categoryMeta.creator;
        if (directory) {
          const directoryHref = category === 'player' ? playerDirectoryHref : peopleDirectoryHref;
          const directoryLabel = category === 'player' ? playerDirectoryLabel : peopleDirectoryLabel;
          if (directoryHref) directory.setAttribute('href', directoryHref);
          if (directoryLabel) directory.textContent = directoryLabel;
        }
        if (!current.length) {
          name.textContent = meta.emptyName || section.dataset.emptyName || '';
          if (kind) kind.textContent = meta.kind;
          if (counter) counter.textContent = '00 / 00';
          if (listTitle) listTitle.textContent = meta.listTitle;
          if (portrait) {
            portrait.hidden = true;
            portrait.removeAttribute('src');
          }
          stage.classList.remove('has-portrait');
          return;
        }
        index = (index + current.length) % current.length;
        const selected = current[index];
        selected.setAttribute('aria-pressed', 'true');
        name.textContent = selected.dataset.peopleName;
        const portraitUrl = selected.dataset.peopleImage;
        if (portrait) {
          portrait.hidden = !portraitUrl;
          if (portraitUrl) portrait.src = portraitUrl;
          else portrait.removeAttribute('src');
          portrait.classList.remove('rocket-image--auto', 'rocket-image--fill', 'rocket-image--original', 'rocket-image--portrait');
          portrait.classList.add(`rocket-image--${selected.dataset.peopleFit || 'auto'}`);
        }
        stage.classList.toggle('has-portrait', Boolean(portraitUrl));
        if (!reducedMotion.matches) {
          name.style.animation = 'none';
          void name.offsetWidth;
          name.style.animation = '';
        }
        if (kind) kind.textContent = meta.kind;
        if (listTitle) listTitle.textContent = meta.listTitle;
        if (counter) counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(current.length).padStart(2, '0')}`;
      }

      portrait?.addEventListener('error', () => {
        portrait.hidden = true;
        stage.classList.remove('has-portrait');
      }, { signal });
      tabs.forEach(tab => {
        tab.addEventListener('click', () => { category = tab.dataset.peopleTab; index = 0; update(); }, { signal });
        tab.addEventListener('keydown', event => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const tabIndex = tabs.indexOf(tab);
          const nextTab = tabs[(tabIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
          nextTab.focus();
          nextTab.click();
        }, { signal });
      });
      people.forEach(person => person.addEventListener('click', () => {
        category = person.dataset.peopleCategory;
        index = currentPeople().indexOf(person);
        update();
      }, { signal }));
      previous?.addEventListener('click', () => { index -= 1; update(); }, { signal });
      next?.addEventListener('click', () => { index += 1; update(); }, { signal });
      stage.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        index += event.key === 'ArrowRight' ? 1 : -1;
        update();
      }, { signal });
      stage.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button === 0) startX = event.clientX;
      }, { signal });
      stage.addEventListener('pointerup', event => {
        if (startX === null) return;
        const delta = event.clientX - startX;
        startX = null;
        if (Math.abs(delta) > 35) {
          index += delta < 0 ? 1 : -1;
          update();
        }
      }, { signal });
      stage.addEventListener('pointercancel', () => { startX = null; }, { signal });
      update();
      return () => controller.abort();
    });
  }

  function initCollection(root) {
    register(root, section => {
      const limit = Number(section.dataset.visibleLimit || 4);
      const cards = [...section.querySelectorAll('[data-collection-item]')];
      cards.forEach((card, index) => {
        const storefrontHidden = card.dataset.editorState === 'hidden' || index >= limit;
        card.hidden = storefrontHidden;
        card.dataset.storefrontHidden = String(storefrontHidden);
      });
      return () => {};
    });
  }

  function initLocalizationSelect(root) {
    register(root, select => {
      const controller = new AbortController();
      select.addEventListener('change', () => select.form?.submit(), { signal: controller.signal });
      return () => controller.abort();
    });
  }

  function initLocalizedNavigation() {
    const root = document.documentElement.dataset.marketRoot || window.Shopify?.routes?.root || '/';
    const prefix = root.replace(/\/+$/, '');
    if (!prefix) return;
    const existingLocale = /^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i;
    const assetPath = /^\/(?:cdn|assets|s\/files)(?:\/|$)/i;

    function localizeLink(link) {
      if (link.hasAttribute('download')) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('?') || href.startsWith('//')) return;
      let target;
      try {
        target = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (target.origin !== window.location.origin || assetPath.test(target.pathname)) return;
      if (target.pathname === prefix || target.pathname.startsWith(prefix + '/') || existingLocale.test(target.pathname)) return;
      target.pathname = prefix + target.pathname;
      const localized = /^https?:\/\//i.test(href)
        ? target.href
        : target.pathname + target.search + target.hash;
      if (localized !== href) link.setAttribute('href', localized);
    }

    document.querySelectorAll('a[href]').forEach(localizeLink);
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes') {
          localizeLink(mutation.target);
          return;
        }
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches('a[href]')) localizeLink(node);
          node.querySelectorAll('a[href]').forEach(localizeLink);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
  }

  function initAll(scope = document) {
    initReveal(scope);
    const roots = selector => {
      const items = [];
      if (scope.matches?.(selector)) items.push(scope);
      items.push(...scope.querySelectorAll?.(selector) || []);
      return items;
    };
    roots('[data-rocket-header]').forEach(initHeader);
    roots('[data-rocket-hero]').forEach(initHero);
    roots('[data-feature-cinema]').forEach(initFeatureCinema);
    roots('[data-rocket-performance]').forEach(initPerformance);
    roots('[data-model-quiz]').forEach(initModelQuiz);
    roots('[data-design-gallery]').forEach(initDesignGallery);
    roots('[data-misaki-section]').forEach(initParallax);
    roots('[data-rocket-people]').forEach(initCommunity);
    roots('[data-shopify-collection]').forEach(initCollection);
    roots('[data-localization-select]').forEach(initLocalizationSelect);
  }

  document.addEventListener('click', event => {
    const link = event.target.closest('[data-model-compare-link]');
    if (!link) return;
    const target = document.querySelector(link.getAttribute('href') || '#model-compare') || document.querySelector('[data-model-compare]');
    if (!target) return;
    event.preventDefault();
    target.open = true;
    target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
  });

  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => cleanupWithin(event.target));
  document.addEventListener('shopify:block:select', event => {
    const feature = event.target.matches?.('[data-feature-index]') ? event.target : event.target.querySelector?.('[data-feature-index]');
    if (feature) feature.click();
    const design = event.target.matches?.('.design-option') ? event.target : event.target.querySelector?.('.design-option');
    if (design) design.click();
    const person = event.target.matches?.('.rocket-people__person') ? event.target : event.target.querySelector?.('.rocket-people__person');
    if (person) person.click();
    const card = event.target.matches?.('[data-collection-item]') ? event.target : event.target.querySelector?.('[data-collection-item]');
    if (card) card.hidden = false;
    const quizStep = event.target.matches?.('[data-quiz-step]') ? event.target : event.target.querySelector?.('[data-quiz-step]');
    if (quizStep) {
      const quiz = quizStep.closest('[data-model-quiz]');
      const steps = [...quiz.querySelectorAll('[data-quiz-step]')];
      quiz.dispatchEvent(new CustomEvent('rocket:quiz-preview', { detail: { step: steps.indexOf(quizStep) } }));
    }
  });
  document.addEventListener('shopify:block:deselect', event => {
    const card = event.target.matches?.('[data-collection-item]') ? event.target : event.target.querySelector?.('[data-collection-item]');
    if (card?.dataset.storefrontHidden === 'true') card.hidden = true;
    const quizStep = event.target.matches?.('[data-quiz-step]') ? event.target : event.target.querySelector?.('[data-quiz-step]');
    quizStep?.closest('[data-model-quiz]')?.dispatchEvent(new CustomEvent('rocket:quiz-reset'));
  });

  initLocalizedNavigation();
  initAll(document);
})();
